import { Injectable } from "@angular/core";
import { ApiService } from "../api.service";
import { SocketService } from "../socket.service";
import { FuturesPositionsService } from "./futures-positions.service";
import { RpcService, ENetwork } from 'src/app/@core/services/rpc.service';
import { ENDPOINTS } from 'src/environments/endpoints.conf';
import { RelayerWsService } from '../relayer-ws.service';

export interface IFuturesMarketType {
    name: string;
    markets: IFutureMarket[];
    icon: string;
    disabled: boolean;
}

export interface IFutureMarket {
    first_token: IToken;
    second_token: IToken;
    disabled: boolean;
    pairString: string;
    contractName: string;
    contract_id: number;
    collateral: IToken;
    leverage?: number; // Newly added
    notional?: number;
    inverse?: boolean;
}

export interface IToken {
    shortName: string;
    fullName: string;
    propertyId: number;
}

@Injectable({
    providedIn: 'root',
})
export class FuturesMarketService {

    private _futuresMarketsTypes: IFuturesMarketType[] = [];
    private _selectedMarketType: IFuturesMarketType = this.futuresMarketsTypes[0] || null;
    private _selectedMarket: IFutureMarket = this.selectedMarketType?.markets[0] || null;
    private _lastNet?: string;
    public isContractDataReady = false; // Guard flag for data readiness

    constructor(
        private apiService: ApiService,
        private socketService: SocketService,
        private futuresPositionsService: FuturesPositionsService,
        private rpcService: RpcService,
        private relayerWsService: RelayerWsService,
    ) {}

    // --- Removed socket getter! ---

    get futuresMarketsTypes(): IFuturesMarketType[] {
        return this._futuresMarketsTypes;
    }

    get selectedMarketType(): IFuturesMarketType {
        return this._selectedMarketType;
    }
	
	get relayerUrl(): string {
	    const net = this.rpcService.NETWORK;
	    console.log('[FMS] rpcService.NETWORK =', net, 'typeof →', typeof net);

	    if (net && typeof net === 'object' && 'relayerUrl' in net) {
	      // @ts-ignore – we know it has relayerUrl
	      const urlFromObj = (net as any).relayerUrl;
	      console.log('[FMS] using relayerUrl on NETWORK object →', urlFromObj);
	      return urlFromObj;
	    }

	    const key = String(net) as ENetwork;
	    console.log('[FMS] coerced network key →', key);

	    if (key === ENetwork.LTCTEST) {
	      const u = ENDPOINTS.LTCTEST.relayerUrl;
	      console.log('[FMS] matched LTCTEST →', u);
	      return u;
	    }

	    const fallback = ENDPOINTS.LTC.relayerUrl;
	    console.log('[FMS] defaulting to LTC →', fallback);
	    return fallback;
	  }

    set selectedMarketType(value: IFuturesMarketType) {
        if (!this.futuresMarketsTypes.length) return;
        this._selectedMarketType = value;
        this.selectedMarket = this.marketsFromSelectedMarketType.find(e => !e.disabled) || this.marketsFromSelectedMarketType[0];
    }

    get selectedMarketTypeIndex() {
        return this.futuresMarketsTypes.indexOf(this.selectedMarketType);
    }

    get marketsFromSelectedMarketType(): IFutureMarket[] {
        if (!this.futuresMarketsTypes.length) return [];
        return this.selectedMarketType.markets;
    }

    get selectedMarket(): IFutureMarket {
        return this._selectedMarket;
    }

    set selectedMarket(value: IFutureMarket) {
        this._selectedMarket = value;
        this.changeOrderbookMarketFilter();
        this.futuresPositionsService.selectedContractId = (this.selectedMarket.contract_id).toString();
        this.futuresPositionsService.updatePositions();
    }

    get selectedMarketIndex() {
        return this.marketsFromSelectedMarketType.indexOf(this.selectedMarket);
    }

    get marketFilter() {
        return {
            type: 'FUTURES',
            contract_id: this.selectedMarket.contract_id,
        };
    }

    getMarkets() {
            const net = this.rpcService.NETWORK;
            if (!net) { 
              // optionally log/return EMPTY or throw
              return; 
            }
            this.apiService.marketApi.getFuturesMarkets(net.toString())
            .subscribe(async (marketTypes: IFuturesMarketType[]) => {
                this._futuresMarketsTypes = marketTypes;
                this._selectedMarketType = marketTypes.find(e => !e.disabled) || marketTypes[0];
                this._selectedMarket = this._selectedMarketType.markets.find(m => !m.disabled) || this._selectedMarketType.markets[0];

                this.futuresPositionsService.selectedContractId = this._selectedMarket.contract_id.toString();
                this.futuresPositionsService.updatePositions();
                this.changeOrderbookMarketFilter();

                // Add contract enrichment
                await this.enrichWithContractInfo();

                // ✅ Ready to render
                this.isContractDataReady = true;
            });
    }

    private changeOrderbookMarketFilter() {
        // Emit the event directly via SocketService
        this.socketService.emitEvent('update-orderbook', this.marketFilter);
    }

    public clearCache() { (this as any)._futuresMarketsTypes = []; } 

    private async enrichWithContractInfo() {
        const allMarkets = this._futuresMarketsTypes
            .map((type: IFuturesMarketType) => type.markets)
            .reduce((acc, val) => acc.concat(val), []);

        for (const market of allMarkets) {
            try {
                const rpcUrl = this.relayerUrl.replace(/\/+$/, '') + '/rpc/tl_listContractSeries';
                const base = rpcUrl.replace(/\/rpc\/tl_listContractSeries$/, '');
                this.relayerWsService.setBaseUrl(base);
                const info = await this.relayerWsService.request<any>(`/rpc/tl_listContractSeries`, {
                  method: "POST",
                  body: {
                    jsonrpc: "2.0",
                    id: 1,
                    params: [market.contract_id]
                  },
                });
                market.leverage = info?.leverage ?? undefined;
                market.notional = info?.notionalValue ?? undefined;
                market.inverse = info?.inverse ?? undefined;
            } catch (err) {
                console.warn(`Failed to load contract info for contract_id ${market.contract_id}`, err);
            }
        }
    }

    getMarketByContractId(contractId: number): IFutureMarket | null {
        const allMarkets = this._futuresMarketsTypes
            .map(type => type.markets)
            .reduce((acc, val) => acc.concat(val), []);
        return allMarkets.find(m => m.contract_id === contractId) || null;
    }
}
